import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
    LayoutDashboard,
    Target,
    LineChart,
    CalendarClock,
    Newspaper,
    BookOpenCheck,
    ListChecks,
    BellRing,
    LogOut,
    Menu,
    X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { nyNow } from "@/lib/time";

const NAV = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/trading", label: "Trading Routine", icon: Target, testid: "nav-trading" },
    { to: "/journal", label: "Journal & Backtests", icon: LineChart, testid: "nav-journal" },
    { to: "/calendar", label: "Economic Calendar", icon: CalendarClock, testid: "nav-calendar" },
    { to: "/news", label: "News & Brief", icon: Newspaper, testid: "nav-news" },
    { to: "/faith", label: "Faith & Discipline", icon: BookOpenCheck, testid: "nav-faith" },
    { to: "/tasks", label: "Personal Life", icon: ListChecks, testid: "nav-tasks" },
    { to: "/reminders", label: "Reminders", icon: BellRing, testid: "nav-reminders" },
];

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const now = nyNow();

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    return (
        <div className="min-h-screen flex relative" data-testid="app-layout">
            {/* Mobile top bar */}
            <div className="md:hidden fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-4 h-14 bg-[#0e0e10]/90 backdrop-blur-xl border-b border-white/5">
                <div className="font-serif text-lg tracking-tight">
                    <span className="gold">T</span>rade With <span className="gold">P</span>urpose
                </div>
                <button
                    onClick={() => setOpen(!open)}
                    className="p-2 rounded-md hover:bg-white/5"
                    aria-label="Toggle menu"
                    data-testid="mobile-menu-btn"
                >
                    {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </div>

            {/* Sidebar */}
            <aside
                className={`${
                    open ? "translate-x-0" : "-translate-x-full"
                } md:translate-x-0 fixed md:sticky top-0 left-0 z-30 h-screen w-72 bg-[#0e0e10] border-r border-white/5 flex flex-col transition-transform md:transition-none pt-16 md:pt-0`}
                data-testid="sidebar"
            >
                <div className="hidden md:block px-6 pt-7 pb-5 border-b border-white/5">
                    <div className="font-serif text-2xl tracking-tight">
                        <span className="gold">T</span>rade With <span className="gold">P</span>urpose
                    </div>
                    <div className="caption mt-2 flex items-center gap-2">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37]" />
                        <span>{now.date}</span>
                        <span className="text-zinc-600">·</span>
                        <span className="font-mono">{now.time}</span>
                    </div>
                </div>

                <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
                    {NAV.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.to === "/"}
                            onClick={() => setOpen(false)}
                            data-testid={item.testid}
                            className={({ isActive }) =>
                                `group flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                                    isActive
                                        ? "bg-[#18181B] text-white border-l-2 border-[#D4AF37] pl-[10px]"
                                        : "text-zinc-400 hover:text-white hover:bg-white/[0.03]"
                                }`
                            }
                        >
                            <item.icon className="w-4 h-4" strokeWidth={1.5} />
                            <span>{item.label}</span>
                        </NavLink>
                    ))}
                </nav>

                <div className="px-4 py-4 border-t border-white/5">
                    <div className="text-sm truncate">{user?.name || user?.email}</div>
                    <div className="caption truncate">{user?.email}</div>
                    <button
                        onClick={handleLogout}
                        data-testid="logout-btn"
                        className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-white/10 hover:border-[#D4AF37]/40 text-sm text-zinc-300 hover:text-white transition-colors"
                    >
                        <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sign out
                    </button>
                </div>
            </aside>

            {/* Main */}
            <main className="flex-1 min-w-0 md:ml-0 pt-14 md:pt-0" data-testid="main-content">
                <div className="max-w-7xl mx-auto px-5 md:px-10 py-8 md:py-10">{children}</div>
            </main>

            {open && (
                <button
                    aria-label="Close menu"
                    className="md:hidden fixed inset-0 bg-black/50 z-20"
                    onClick={() => setOpen(false)}
                />
            )}
        </div>
    );
}
